/**
 * Sequência linear das etapas do fluxo legado, para os botões
 * "‹ Anterior / Próxima ›" (problema #7 / F10).
 *
 * Ordem confirmada com a usuária (2026-05-31): começa na Liberação e
 * percorre o grupo "Escritório" da sidebar (sem o Acompanhamento, que é
 * painel de visão geral, não etapa); ao passar do PPCP, cai em Compras e
 * percorre o grupo "Fábrica" inteiro até o Histórico de Entregas.
 *
 * Fonte de verdade única — tanto o componente quanto os testes consomem
 * daqui. As rotas batem com as de App.jsx e da sidebar (utils/modules.jsx).
 */

// `requires`: permissao `recurso:view` da pagina (espelha utils/modules.jsx).
// Usada para que as setas pulem as paginas sem acesso (P3, 2026-06-18).
export const FLUXO_ETAPAS = [
  // Escritório (a partir da Liberação)
  { label: "Liberação", to: "/liberacao", requires: "liberacao:view" },
  { label: "Acabamentos", to: "/acabamentos", requires: "acabamentos:view" },
  { label: "Medição", to: "/medicao", requires: "medicao:view" },
  { label: "Executivo", to: "/executivo", requires: "executivo:view" },
  // Revisão antes da aprovação (28/10/2026), a mesma ordem da sidebar.
  { label: "Revisão", to: "/revisao", requires: "revisao:view" },
  { label: "Aprovação", to: "/aprovacao", requires: "aprovacao:view" },
  { label: "PPCP", to: "/ppcp", requires: "ppcp:view" },
  // Fábrica (ordem completa)
  { label: "Compras", to: "/compras", requires: "compras:view" },
  { label: "Ferragens", to: "/ferragens", requires: "ferragens:view" },
  { label: "Máquinas", to: "/industrializacao", requires: "industrializacao:view" },
  { label: "Marcenaria", to: "/marcenaria", requires: "marcenaria:view" },
  { label: "Terceiros", to: "/terceiros", requires: "terceiros:view" },
  { label: "Pintura & Lacagem", to: "/acabamento", requires: "acabamento:view" },
  { label: "Entregas", to: "/entregas", requires: "entregas:view" },
  { label: "Histórico de Entregas", to: "/obras", requires: "obras:view" },
];

/**
 * Índice da etapa cuja rota bate exatamente com o pathname dado.
 * As rotas do fluxo são exatas (sem sub-rotas), então comparação direta
 * basta e evita falso-positivo entre /acabamento e /acabamentos.
 * @returns {number} índice em FLUXO_ETAPAS, ou -1 se não for etapa do fluxo.
 */
export function indiceDaRota(pathname) {
  if (!pathname) return -1;
  return FLUXO_ETAPAS.findIndex((etapa) => etapa.to === pathname);
}

/**
 * Vizinhança da etapa atual para renderizar a navegação, considerando o
 * ACESSO do usuário (P3): a anterior/próxima é a etapa ACESSÍVEL mais próxima
 * em cada sentido — as páginas sem acesso são puladas. Quando não há nenhuma
 * etapa acessível num sentido, aquele vizinho é `null` (a seta some).
 *
 * @param {string} pathname
 * @param {(etapa: {requires?: string}) => boolean} temAcesso predicado de
 *   acesso por etapa (default: tudo acessível, p/ retrocompat dos testes).
 * @returns {null | {
 *   indice: number, total: number,
 *   atual: {label, to},
 *   anterior: {label, to} | null,
 *   proxima: {label, to} | null,
 * }} null quando a rota não pertence ao fluxo.
 */
export function vizinhosDaRota(pathname, temAcesso = () => true) {
  const indice = indiceDaRota(pathname);
  if (indice < 0) return null;

  let proxima = null;
  for (let i = indice + 1; i < FLUXO_ETAPAS.length; i += 1) {
    if (temAcesso(FLUXO_ETAPAS[i])) {
      proxima = FLUXO_ETAPAS[i];
      break;
    }
  }
  let anterior = null;
  for (let i = indice - 1; i >= 0; i -= 1) {
    if (temAcesso(FLUXO_ETAPAS[i])) {
      anterior = FLUXO_ETAPAS[i];
      break;
    }
  }

  return {
    indice,
    total: FLUXO_ETAPAS.length,
    atual: FLUXO_ETAPAS[indice],
    anterior,
    proxima,
  };
}
